import negate from 'lodash/negate'
import { nanoid } from 'nanoid/non-secure'
import { serializeError } from 'serialize-error'

import type { UploaderUppy } from './createUppy'
import type { BufferUpload, S3Upload, Upload } from './entities/Upload'
import logger, { logError } from './logger'
import { fileFromUrl } from './remoteFile'
import type { RemoteFile } from './remoteFile'
import { assertUppyMetadata } from './uppy/assertUppyMetadata'
import { getUploadServiceErrorMessage } from './uppy/uploadServiceError'
import { UppyFile } from './uppy/UppyFile'
import type {
  UploadResult,
  UppyFile as BaseUppyFile,
  UppyFileDescriptor,
} from './uppy/UppyFile'
import {
  validateFile,
  validateFilesForUploads,
} from './validation/validateFilesForUpload'
import type { FileRejection } from './validation/validateFilesForUpload'
import { hasMixedMediaTypes } from './validation/validationUtils/hasMixedMediaTypes'
import { LocalFile } from './values/LocalFile'
import type { UploaderRestrictions } from './values/UploaderRestrictions'
import { UploadMetadata } from './values/UploadMetadata'
import { UploadProgress } from './values/UploadProgress'
import { WebEventEmitter } from './WebEventEmitter'

export type UploadFinishedCallback = (upload: BufferUpload) => void
type UploadFileMetadata = Pick<UploadMetadata, 'source' | 'isReplacement'>
type EmmittedError = Error & {
  __emmitted?: boolean
}

// Uppy types its error payloads structurally, but emits real Errors. Keeping the
// original instance matters: emitUploadFailed dedupes by flagging the object.
function toEmittedError(error: {
  name: string
  message: string
}): EmmittedError {
  return error instanceof Error
    ? error
    : Object.assign(new Error(error.message), error)
}

function getCauseMessage(error: Error): string | undefined {
  const { cause } = error

  if (
    typeof cause === 'object' &&
    cause !== null &&
    'message' in cause &&
    typeof cause.message === 'string'
  ) {
    return cause.message
  }

  return undefined
}

export type UploaderEvents = {
  'file-fetch-failed': (file: RemoteFile, error: Error) => void
  'file-upload-attempted': (files: LocalFile[]) => void
  'file-validation-failed': (file: LocalFile, error: Error) => void
  'upload-prevented': (error: Error, files?: LocalFile[]) => void
  'upload-started': (upload: Upload) => void
  'upload-progress': (
    event: Pick<Upload, 'id'>,
    progress: UploadProgress,
  ) => void
  'upload-to-s3-finished': (upload: S3Upload) => void
  'upload-finished': UploadFinishedCallback
  'upload-removed': (uploadId: string) => void
  'upload-failed': (event: Upload, error: Error) => void
  'uploader-closed': (uploaderId: string) => void
}

export function isChildUpload(upload: BaseUppyFile): boolean {
  return !!upload.meta.parentUploadId
}

export class Uploader extends WebEventEmitter<UploaderEvents> {
  private readonly preparationController = new AbortController()
  private metadataMap: WeakMap<File, UploadMetadata> = new WeakMap()
  private readonly getDefaultCount: () => number = () =>
    this.uppyInstance.getFiles().filter(negate(isChildUpload)).length

  constructor(
    private readonly id: string,
    private readonly organizationId: string,
    public readonly uppyInstance: UploaderUppy,
    private readonly fileRestrictions: UploaderRestrictions,
    private readonly getCustomCount?: () => number,
    private readonly onUploadInitiated?: () => void,
    prepareFile?: (file: File, signal: AbortSignal) => Promise<File>,
  ) {
    super()

    this.mapUppyEventsToUploaderEvents()

    if (prepareFile) {
      this.uppyInstance.addPreProcessor(async (fileIds) => {
        for (const fileId of fileIds) {
          const file = this.uppyInstance.getFile(fileId)
          if (!file || !(file.data instanceof File)) continue

          try {
            const prepared = await prepareFile(
              file.data,
              this.preparationController.signal,
            )
            if (!this.uppyInstance.getFile(fileId)) continue
            if (prepared === file.data) continue

            const result = await validateFile(prepared, {
              fileRestrictions: this.fileRestrictions,
            })
            if (!(result instanceof File)) throw new Error(result.message)

            this.uppyInstance.setFileState(fileId, {
              data: prepared,
              name: prepared.name,
              type: prepared.type,
              size: prepared.size,
              extension: prepared.name.split('.').pop(),
              meta: { ...file.meta, name: prepared.name, type: prepared.type },
            })
          } catch (error) {
            if (!this.uppyInstance.getFile(fileId)) continue
            const failure =
              error instanceof Error
                ? error
                : new Error('File preparation failed')
            this.emitUploadFailed(UppyFile.toUpload(file), failure)
            this.uppyInstance.removeFile(fileId)
          }
        }
      })
    }
  }

  getId(): string {
    return this.id
  }

  getOrganizationId(): string {
    return this.organizationId
  }

  canUploadFileType(fileType: string): boolean {
    return this.fileRestrictions.uploadConfig.has(fileType)
  }

  /*
   * Add file and start upload
   */
  async addFiles(files: File[], meta?: UploadFileMetadata): Promise<void> {
    this.onUploadInitiated?.()
    logger('Adding %i files', files.length)
    this.generateMetadata(files, meta)

    this.emitFileUploadAttempted(files)

    const fileRestrictions = this.fileRestrictions

    if (fileRestrictions.mixedMediaError && hasMixedMediaTypes(files)) {
      const error = new Error(fileRestrictions.mixedMediaError)
      this.emitUploadPrevented(error, files)

      return
    }

    // Check if any of the new files replace existing media.
    // Image editors set isReplacement so the upload does not consume another slot.
    const replacementCount = files.filter((file) => {
      const metadata = this.metadataMap.get(file)
      return metadata?.isReplacement === true
    }).length

    const { validFiles, invalidFiles } = await validateFilesForUploads(files, {
      fileRestrictions,
      currentFileCount: Math.max(0, this.getUploadsCount() - replacementCount),
    })

    this.emitFileValidationFailed(invalidFiles)

    const descriptors = validFiles.map((file) => this.getUppyDescriptor(file))

    this.uppyInstance.addFiles(descriptors)
  }

  /*
   * Add files and start upload with a promise returned
   */
  async upload(
    files: File[],
    meta?: UploadFileMetadata,
  ): Promise<UploadResult> {
    await this.addFiles(files, meta)

    const result = await this.uppyInstance.upload()

    return {
      successful: (result?.successful ?? []).filter(
        UppyFile.isUploadedUppyFile,
      ),
      failed: result?.failed ?? [],
    }
  }

  /**
   * addFilesFromUrls
   *
   * When urls are provided instead files, we can use this method to get
   * the content of the files and add files to the uploader finally.
   */
  async addFilesFromUrls(
    remoteFiles: RemoteFile | RemoteFile[],
    meta: UploadFileMetadata,
  ): Promise<UploadResult> {
    this.onUploadInitiated?.()
    if (!Array.isArray(remoteFiles)) {
      remoteFiles = [remoteFiles]
    }

    const downloadedFiles = await Promise.allSettled(
      remoteFiles.map(async (remoteFile) => {
        logger('Fetching file from url: %s', remoteFile.remoteUrl)

        try {
          return await fileFromUrl(remoteFile)
        } catch (error) {
          if (error instanceof Error) {
            this.emit('file-fetch-failed', remoteFile, error)
          }
        }
      }),
    )

    return this.upload(
      downloadedFiles
        .filter(
          (result): result is PromiseFulfilledResult<File> =>
            result.status === 'fulfilled',
        )
        .map((result) => result.value),
      meta,
    )
  }

  close(cleanup?: () => void): void {
    this.preparationController.abort()
    this.metadataMap = new WeakMap()

    this.uppyInstance.destroy()
    this.emit('uploader-closed', this.id)
    this.offAll()
    cleanup?.()
  }

  removeUpload(upload: Upload): void {
    this.uppyInstance.removeFile(upload.id)
  }

  reportError<T extends object>(error: Error, file: T): void {
    const shouldDebug = 'cause' in error || 'isNetworkError' in error
    logError(error, {
      context: `uploader.${this.getId()}`,
      metaData: {
        file,
        debugInfo: shouldDebug && serializeError(error),
      },
    })
  }

  private getUploadsCount(): number {
    return this.getCustomCount?.() ?? this.getDefaultCount()
  }

  private emitFileValidationFailed(invalidFiles: FileRejection[]): void {
    invalidFiles.forEach(({ file, message }) => {
      this.emit(
        'file-validation-failed',
        this.getLocalFile(file),
        new Error(message),
      )
    })
  }

  private emitFileUploadAttempted(files: File[]): void {
    this.emit(
      'file-upload-attempted',
      files.map((file) => this.getLocalFile(file)),
    )
  }

  private emitUploadPrevented(error: Error, files: File[]): void {
    this.emit(
      'upload-prevented',
      error,
      files.map((file) => this.getLocalFile(file)),
    )
  }

  private generateMetadata(files: File[], meta: UploadFileMetadata = {}): void {
    files.forEach((file) => {
      if (this.metadataMap.has(file)) {
        // we should only generate metadata once
        logger('Warning: Metadata already exists for file: %s', file.name)
        return
      }

      logger('Generating metadata for file: %s', file.name)
      this.metadataMap.set(
        file,
        UploadMetadata.new({
          ...meta,
          organizationId: this.organizationId,
          trackingId: nanoid(),
          uploaderId: this.id,
        }),
      )
    })
  }

  private getMetadata(file: File): UploadMetadata {
    const metadata = this.metadataMap.get(file)

    if (!metadata) throw new Error('Metadata not found for file')

    // Spreading into a new object because Uppy mutates the metadata in some cases
    return { ...metadata }
  }

  /**
   * Uppy sometimes emits the same error multiple times. This method ensures
   */
  private emitUploadFailed(upload: Upload, error: EmmittedError): void {
    if (!error.__emmitted) {
      const reason =
        getUploadServiceErrorMessage(error) ||
        getCauseMessage(error) ||
        error.message

      error.message = `Upload failed for ${upload.name}. ${reason}`
      this.emit('upload-failed', upload, error)
      error.__emmitted = true
    }
  }

  /**
   * Translates uppy events into uploader events.
   *
   * This hides implementation details (i.e. uppy dependency) and allows for
   * emitting directly from the uploader instance
   */
  private mapUppyEventsToUploaderEvents(): void {
    this.uppyInstance.on('restriction-failed', (file, error) => {
      logger('Restriction failed for %s: %s', file?.name, error.message)

      if (!(file?.data instanceof File)) {
        this.emitUploadPrevented(error, [])

        return
      }

      const localFile = this.getLocalFile(file.data)

      this.emit('file-validation-failed', localFile, error)
    })

    this.uppyInstance.on('upload-error', (file, error) => {
      if (file && !isChildUpload(file)) {
        assertUppyMetadata(file)

        const upload = UppyFile.toUpload(file)

        this.emitUploadFailed(upload, toEmittedError(error))
      }
    })

    this.uppyInstance.on('file-removed', (file) => {
      if (!isChildUpload(file)) {
        this.emit('upload-removed', file.id)
      }
    })

    this.uppyInstance.on('file-added', (file) => {
      if (!isChildUpload(file)) {
        assertUppyMetadata(file)

        const upload = UppyFile.toUpload(file)
        this.emit('upload-started', upload)
      }
    })

    this.uppyInstance.on('upload-progress', (file, uppyProgress) => {
      if (file && !isChildUpload(file)) {
        const progress = UploadProgress.new(
          uppyProgress.bytesTotal,
          uppyProgress.bytesUploaded,
        )
        this.emit('upload-progress', { id: file.id }, progress)
      }
    })
  }

  private getLocalFile(file: File): LocalFile {
    if (!this.metadataMap.has(file)) {
      this.generateMetadata([file])
    }

    return LocalFile.new(file, this.getMetadata(file))
  }

  private getUppyDescriptor(file: File): UppyFileDescriptor {
    const { meta, ...fileDetails } = this.getLocalFile(file)

    return {
      ...fileDetails,
      meta: {
        ...meta,
        // we reuse the trackingId which is unique as a relativePath
        // so that Uppy does not complain about duplicate files
        relativePath: meta.trackingId,
      },
    }
  }
}
